import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {Evento} from "./models/evento";
import {TarefaService} from "../../../modules/juridico/services/tarefa-service";
import {ItemTarefa} from "../../../modules/juridico/models/lista-tarefa";

@Component({
  selector: 'app-calendario',
  standalone: false,
  templateUrl: './calendario.component.html',
  styleUrl: './calendario.component.css'
})
export class CalendarioComponent implements OnInit {
  @Input() evento = false;
  @Input() titulo = '';
  @Output() diaClicado = new EventEmitter<Date>();
  eventos: Evento[] = [];
  title = '';

  monthNames: string[] = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  currentDate: Date = new Date();
  viewDate: Date = new Date();
  viewMode: 'dia' | 'semana' | 'mes' = 'mes';
  weeks: any[][] = [];
  days: any[] = []; // Para visão semanal e diária

  constructor(private tarefaService: TarefaService) {
    this.viewDate.setHours(0, 0, 0, 0);
  }

  ngOnInit(): void {
    this.carregarDados();
  }

  carregarDados(): void {
    this.buscarTarefas();
    this.updateView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateView();
  }

  updateView(): void {
    if (this.viewMode === 'mes') {
      this.generateMonthCalendar();
    } else if (this.viewMode === 'semana') {
      this.generateWeekCalendar();
    } else {
      this.generateDayCalendar();
    }
  }

  setViewMode(mode: 'dia' | 'semana' | 'mes'): void {
    this.viewMode = mode;
    this.updateView();
  }

  private generateMonthCalendar(): void {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const totalDays = lastDay.getDate();

    this.title = `${this.titulo} - ${this.monthNames[month]} ${year}`;
    let weeks = [];
    let week = new Array(7).fill(null);
    let day = 1;

    for (let i = 0; i < 7; i++) {
      if (i >= startDayOfWeek) {
        week[i] = this.buildDayObject(new Date(year, month, day++));
      }
    }
    weeks.push(week);

    while (day <= totalDays) {
      week = new Array(7).fill(null);
      for (let i = 0; i < 7 && day <= totalDays; i++) {
        week[i] = this.buildDayObject(new Date(year, month, day++));
      }
      weeks.push(week);
    }

    this.weeks = weeks;
  }

  private generateWeekCalendar(): void {
    const startOfWeek = new Date(this.viewDate);
    startOfWeek.setDate(this.viewDate.getDate() - this.viewDate.getDay());

    this.days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      this.days.push(this.buildDayObject(date));
    }

    const firstDay = this.days[0].date;
    const lastDay = this.days[6].date;

    if (firstDay.getMonth() === lastDay.getMonth()) {
      this.title = `${this.titulo} - ${this.monthNames[firstDay.getMonth()]} ${firstDay.getFullYear()}`;
    } else {
      this.title = `${this.titulo} - ${this.monthNames[firstDay.getMonth()]} / ${this.monthNames[lastDay.getMonth()]} ${lastDay.getFullYear()}`;
    }
  }

  private generateDayCalendar(): void {
    this.days = [this.buildDayObject(new Date(this.viewDate))];
    this.title = `${this.titulo} - ${this.viewDate.getDate()} de ${this.monthNames[this.viewDate.getMonth()]} de ${this.viewDate.getFullYear()}`;
  }

  private buildDayObject(date: Date) {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    return {
      day: date.getDate(),
      date: new Date(date),
      dataComparacao: dateStr,
      dayName: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.getDay()]
    };
  }

  goToPrevious(): void {
    if (this.viewMode === 'mes') {
      this.viewDate.setMonth(this.viewDate.getMonth() - 1);
    } else if (this.viewMode === 'semana') {
      this.viewDate.setDate(this.viewDate.getDate() - 7);
    } else {
      this.viewDate.setDate(this.viewDate.getDate() - 1);
    }
    this.updateView();
  }

  goToNext(): void {
    if (this.viewMode === 'mes') {
      this.viewDate.setMonth(this.viewDate.getMonth() + 1);
    } else if (this.viewMode === 'semana') {
      this.viewDate.setDate(this.viewDate.getDate() + 7);
    } else {
      this.viewDate.setDate(this.viewDate.getDate() + 1);
    }
    this.updateView();
  }

  goToToday(): void {
    this.viewDate = new Date();
    this.viewDate.setHours(0, 0, 0, 0);
    this.updateView();
  }

  isToday(dayObj: any): boolean {
    if (!dayObj) return false;
    const today = new Date();
    return dayObj.date.getDate() === today.getDate() &&
      dayObj.date.getMonth() === today.getMonth() &&
      dayObj.date.getFullYear() === today.getFullYear();
  }

  private buscarTarefas(){
    this.tarefaService.minhasTarefas().subscribe(retorno => {
      if(retorno.sucesso){
        this.eventos = this.converterTarefasParaEventos(retorno.dados.itens);
      }
    });
  }

  private converterTarefasParaEventos(tarefas: ItemTarefa[]): Evento[] {
    let eventos: Evento[] = [];

    for(let tarefa of tarefas){
      const date = new Date(tarefa.dataInicial);
      const hora = date.getHours().toString().padStart(2, '0');
      const minutos = date.getMinutes().toString().padStart(2, '0');
      const horario = `${hora}:${minutos}`;

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const dataFormatada = `${year}-${month}-${day}`;

      let evento = new Evento();
      evento.data = dataFormatada;
      evento.hora = horario;
      evento.descricao = tarefa.descricao;
      evento.id = tarefa.id;

      eventos.push(evento);
    }
    return eventos;
  }

  getEventosFiltrados(dataComparacao: string): Evento[]{
    if(this.eventos.length === 0)
      return [];


    let eventosFiltrados = this.eventos.filter(evento => evento.data === dataComparacao);


    return eventosFiltrados;
  }

  onDiaClick(dayObj: any): void {
    if (dayObj && dayObj.date) {
      this.diaClicado.emit(dayObj.date);
    }
  }
}

